import { useCallback, useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { LocalDate } from "@js-joda/core";
import type { StaffProfileData } from "services/api/company/staff.service";
import type {
  EmploymentDetailsForm,
  EmploymentSaveMode,
  PaymentMethodDetailsForm,
} from "../profilePanel.contract";
import type {
  DiscardChangesTarget,
  ModalState,
  TranslateFn,
} from "./profilePanel.state.contract";
import type { DraftSalaryItem } from "./profilePanel.state.salary.logic";
import type { DraftTaxItem } from "./profilePanel.state.tax.logic";
import { useProfilePanelChangeToPermanentModalState } from "./modals/profilePanel.changeToPermanentModal.logic";
import { useProfilePanelEndEmploymentModalState } from "./modals/profilePanel.endEmploymentModal.logic";
import { useProfilePanelPaymentMethodModalState } from "./modals/profilePanel.paymentMethodModal.logic";
import { useProfilePanelSalaryModalState } from "./modals/profilePanel.salaryModal.logic";
import { useProfilePanelTaxModalState } from "./modals/profilePanel.taxModal.logic";
import { EmploymentUiRules } from "../../shared/staffEmploymentRules.logic";

interface UseProfilePanelModalsStateParams {
  companyId: number | null;
  currentEmploymentId: number | null;
  currentEmploymentStartDate: LocalDate | null;
  editedEmploymentDetails: EmploymentDetailsForm;
  editedPaymentMethodDetails: PaymentMethodDetailsForm;
  employmentRules: EmploymentUiRules;
  executeSavingAction: (action: () => Promise<void>) => void;
  isOpen: boolean;
  isSaving: boolean;
  onWorkerUpdated?: () => void;
  profile: StaffProfileData | null;
  reloadProfile: () => void;
  setEditedEmploymentDetails: Dispatch<SetStateAction<EmploymentDetailsForm>>;
  setEditedPaymentMethodDetails: Dispatch<
    SetStateAction<PaymentMethodDetailsForm>
  >;
  setEditingTab: (value: "profile" | "employmentSalary" | null) => void;
  setEmploymentSaveMode: (value: EmploymentSaveMode) => void;
  setHasTouchedPaymentMethodValue: (value: boolean) => void;
  setIsSaving: (value: boolean) => void;
  setSaveError: (value: string) => void;
  translate: TranslateFn;
  workerId: number | null;
}

interface UseProfilePanelModalsStateResult {
  salaryDraftHistory: DraftSalaryItem[];
  taxDraftHistory: DraftTaxItem[];
  effectiveFromDate: LocalDate | null;
  endEmploymentDate: LocalDate | null;
  hasEndedEmploymentForCreateFlow: boolean;
  readonly isChangeToPermanentModalOpen: boolean;
  isDiscardConfirmOpen: boolean;
  isSalaryModalOpen: boolean;
  isTaxModalOpen: boolean;
  isPaymentMethodModalOpen: boolean;
  isEndEmploymentModalOpen: boolean;
  isEndEmploymentForCreateModalOpen: boolean;
  setEffectiveFromDate: (date: LocalDate | null) => void;
  setEndEmploymentDate: (date: LocalDate | null) => void;
  openEndEmploymentForCreateModal: () => void;
  handleOpenSalaryModal: () => void;
  handleCloseSalaryModal: () => void;
  handleConfirmSalaryModal: () => void;
  handleAddSalaryModal: () => void;
  handleDeleteSalaryModal: (salaryId: number) => void;
  handleUpdateSalaryDraftValue: (salaryId: number, value: number) => void;
  readonly handleUpdateTaxDraftValue: (taxId: number, value: number) => void;
  handleOpenTaxModal: () => void;
  handleCloseTaxModal: () => void;
  handleConfirmTaxModal: () => void;
  handleAddTaxModal: () => void;
  handleDeleteTaxModal: (taxId: number) => void;
  handleOpenPaymentMethodModal: () => void;
  handleClosePaymentMethodModal: () => void;
  handleConfirmPaymentMethodModal: () => void;
  handleDeletePaymentMethodModal: () => void;
  handleOpenEndEmploymentModal: () => void;
  handleCloseEndEmploymentModal: () => void;
  handleConfirmEndEmploymentModal: () => void;
  handleCloseEndEmploymentForCreateModal: () => void;
  handleConfirmEndEmploymentForCreateModal: () => void;
  readonly handleCloseChangeToPermanentModal: () => void;
  readonly handleConfirmChangeToPermanent: () => void;
  readonly handleOpenChangeToPermanentModal: () => void;
  handleConfirmDiscardClose: () => void;
  handleCancelDiscardClose: () => void;
}

export const useProfilePanelModalsState = (
  params: UseProfilePanelModalsStateParams,
): UseProfilePanelModalsStateResult => {
  const [modalState, setModalState] = useState<ModalState>({ type: "none" });
  const [discardChangesTarget, setDiscardChangesTarget] =
    useState<DiscardChangesTarget>(null);
  const [effectiveFromDate, setEffectiveFromDate] = useState<LocalDate | null>(
    null,
  );
  const [endEmploymentDate, setEndEmploymentDate] = useState<LocalDate | null>(
    null,
  );

  useEffect(() => {
    if (!params.isOpen) {
      setModalState({ type: "none" });
      setDiscardChangesTarget(null);
      setEffectiveFromDate(null);
      setEndEmploymentDate(null);
      return;
    }

    setModalState({ type: "none" });
    setDiscardChangesTarget(null);
    setEndEmploymentDate(null);
  }, [params.isOpen, params.workerId]);
  const salaryModalState = useProfilePanelSalaryModalState({
    companyId: params.companyId,
    currentEmploymentId: params.currentEmploymentId,
    currentEmploymentStartDate: params.currentEmploymentStartDate,
    editedEmploymentDetails: params.editedEmploymentDetails,
    effectiveFromDate,
    executeSavingAction: params.executeSavingAction,
    isOpen: params.isOpen,
    isSaving: params.isSaving,
    onWorkerUpdated: params.onWorkerUpdated,
    profile: params.profile,
    reloadProfile: params.reloadProfile,
    setDiscardChangesTarget,
    setEditedEmploymentDetails: params.setEditedEmploymentDetails,
    setEffectiveFromDate,
    setModalState,
    setSaveError: params.setSaveError,
    translate: params.translate,
    workerId: params.workerId,
  });

  const taxModalState = useProfilePanelTaxModalState({
    companyId: params.companyId,
    currentEmploymentStartDate: params.currentEmploymentStartDate,
    editedEmploymentDetails: params.editedEmploymentDetails,
    effectiveFromDate,
    executeSavingAction: params.executeSavingAction,
    isOpen: params.isOpen,
    isSaving: params.isSaving,
    onWorkerUpdated: params.onWorkerUpdated,
    profile: params.profile,
    reloadProfile: params.reloadProfile,
    setDiscardChangesTarget,
    setEditedEmploymentDetails: params.setEditedEmploymentDetails,
    setEffectiveFromDate,
    setModalState,
    setSaveError: params.setSaveError,
    translate: params.translate,
    workerId: params.workerId,
  });

  const paymentMethodModalState = useProfilePanelPaymentMethodModalState({
    companyId: params.companyId,
    editedPaymentMethodDetails: params.editedPaymentMethodDetails,
    executeSavingAction: params.executeSavingAction,
    isOpen: params.isOpen,
    isSaving: params.isSaving,
    onWorkerUpdated: params.onWorkerUpdated,
    profile: params.profile,
    reloadProfile: params.reloadProfile,
    setDiscardChangesTarget,
    setEditedPaymentMethodDetails: params.setEditedPaymentMethodDetails,
    setHasTouchedPaymentMethodValue: params.setHasTouchedPaymentMethodValue,
    setModalState,
    setSaveError: params.setSaveError,
    translate: params.translate,
    workerId: params.workerId,
  });

  const endEmploymentModalState = useProfilePanelEndEmploymentModalState({
    companyId: params.companyId,
    currentEmploymentStartDate: params.currentEmploymentStartDate,
    endEmploymentDate,
    employmentRules: params.employmentRules,
    isOpen: params.isOpen,
    isSaving: params.isSaving,
    onWorkerUpdated: params.onWorkerUpdated,
    profile: params.profile,
    reloadProfile: params.reloadProfile,
    setEditedEmploymentDetails: params.setEditedEmploymentDetails,
    setEditingTab: params.setEditingTab,
    setEmploymentSaveMode: params.setEmploymentSaveMode,
    setEndEmploymentDate,
    setIsSaving: params.setIsSaving,
    setModalState,
    setSaveError: params.setSaveError,
    translate: params.translate,
    workerId: params.workerId,
  });

  const changeToPermanentModalState =
    useProfilePanelChangeToPermanentModalState({
      companyId: params.companyId,
      isSaving: params.isSaving,
      profile: params.profile,
      workerId: params.workerId,
      onWorkerUpdated: params.onWorkerUpdated,
      reloadProfile: params.reloadProfile,
      setIsSaving: params.setIsSaving,
      setModalState,
      setSaveError: params.setSaveError,
      translate: params.translate,
    });

  const handleConfirmDiscardClose = useCallback((): void => {
    if (
      discardChangesTarget === "salary" ||
      discardChangesTarget === "tax" ||
      discardChangesTarget === "paymentMethod"
    ) {
      params.setSaveError("");
      setModalState({ type: "none" });
    }

    setDiscardChangesTarget(null);
  }, [discardChangesTarget, params]);

  const handleCancelDiscardClose = useCallback((): void => {
    setDiscardChangesTarget(null);
  }, []);

  return {
    salaryDraftHistory: salaryModalState.salaryDraftHistory,
    taxDraftHistory: taxModalState.taxDraftHistory,
    effectiveFromDate,
    endEmploymentDate,
    hasEndedEmploymentForCreateFlow:
      endEmploymentModalState.hasEndedEmploymentForCreateFlow,
    isChangeToPermanentModalOpen: modalState.type === "changeToPermanent",
    isDiscardConfirmOpen: discardChangesTarget != null,
    isSalaryModalOpen: modalState.type === "editSalary",
    isTaxModalOpen: modalState.type === "editTax",
    isPaymentMethodModalOpen: modalState.type === "editPaymentMethod",
    isEndEmploymentModalOpen: modalState.type === "endEmployment",
    isEndEmploymentForCreateModalOpen:
      modalState.type === "endEmploymentForCreate",
    setEffectiveFromDate,
    setEndEmploymentDate,
    openEndEmploymentForCreateModal:
      endEmploymentModalState.openEndEmploymentForCreateModal,
    handleOpenSalaryModal: salaryModalState.handleOpenSalaryModal,
    handleCloseSalaryModal: salaryModalState.handleCloseSalaryModal,
    handleConfirmSalaryModal: salaryModalState.handleConfirmSalaryModal,
    handleAddSalaryModal: salaryModalState.handleAddSalaryModal,
    handleDeleteSalaryModal: salaryModalState.handleDeleteSalaryModal,
    handleUpdateSalaryDraftValue: salaryModalState.handleUpdateSalaryDraftValue,
    handleOpenTaxModal: taxModalState.handleOpenTaxModal,
    handleCloseTaxModal: taxModalState.handleCloseTaxModal,
    handleConfirmTaxModal: taxModalState.handleConfirmTaxModal,
    handleAddTaxModal: taxModalState.handleAddTaxModal,
    handleDeleteTaxModal: taxModalState.handleDeleteTaxModal,
    handleUpdateTaxDraftValue: taxModalState.handleUpdateTaxDraftValue,
    handleOpenPaymentMethodModal:
      paymentMethodModalState.handleOpenPaymentMethodModal,
    handleClosePaymentMethodModal:
      paymentMethodModalState.handleClosePaymentMethodModal,
    handleConfirmPaymentMethodModal:
      paymentMethodModalState.handleConfirmPaymentMethodModal,
    handleDeletePaymentMethodModal:
      paymentMethodModalState.handleDeletePaymentMethodModal,
    handleOpenEndEmploymentModal:
      endEmploymentModalState.handleOpenEndEmploymentModal,
    handleCloseEndEmploymentModal:
      endEmploymentModalState.handleCloseEndEmploymentModal,
    handleConfirmEndEmploymentModal:
      endEmploymentModalState.handleConfirmEndEmploymentModal,
    handleCloseEndEmploymentForCreateModal:
      endEmploymentModalState.handleCloseEndEmploymentForCreateModal,
    handleConfirmEndEmploymentForCreateModal:
      endEmploymentModalState.handleConfirmEndEmploymentForCreateModal,
    handleOpenChangeToPermanentModal:
      changeToPermanentModalState.handleOpenChangeToPermanentModal,
    handleCloseChangeToPermanentModal:
      changeToPermanentModalState.handleCloseChangeToPermanentModal,
    handleConfirmChangeToPermanent:
      changeToPermanentModalState.handleConfirmChangeToPermanent,
    handleConfirmDiscardClose,
    handleCancelDiscardClose,
  };
};
